/**
 * Parse template field definitions from ACC template metadata
 * Extract field labels, IDs, and value types from template layout
 */

/**
 * Extract all fields from a template layout
 * @param {Object} template - Template metadata from ACC
 * @returns {Array} Array of field definitions { fieldId, label, valueType, sectionLabel }
 */
export const parseTemplateFields = (template) => {
  const fields = []

  if (!template?.pg_form?.file?.layout?.children) {
    return fields
  }

  const sections = template.pg_form.file.layout.children

  sections.forEach(section => {
    const sectionLabel = section.label || ''
    const sectionFields = section.children || []

    sectionFields.forEach(field => {
      if (field.type === 'field' && field.schema) {
        fields.push({
          fieldId: field.schema,
          label: field.label || 'Untitled Field',
          valueType: field.value_name || 'text_val', // number_val, text_val, date_val, etc.
          sectionLabel: sectionLabel,
          isRequired: field.is_required || false
        })
      }
    })
  })

  return fields
}

/**
 * Extract field value from form response
 * @param {Object} report - Form/report data
 * @param {string} fieldId - Field ID to extract
 * @param {string} valueType - Type of value (number_val, text_val, etc.)
 * @returns {*} Field value or null
 */
export const getFieldValueFromReport = (report, fieldId, valueType = 'number_val') => {
  if (!report?.nativeForm?.customValues) {
    return null
  }

  const fieldData = report.nativeForm.customValues.find(cv => (cv.fieldId || cv.id) === fieldId)

  if (!fieldData || fieldData.deleted) {
    return null
  }

  // Special handling for toggle fields - they're stored as toggleVal or boolVal in ACC
  if (valueType === 'toggle_val') {
    let rawValue = null

    // Check if we have a toggleVal property
    if (fieldData.toggleVal !== undefined) {
      rawValue = fieldData.toggleVal
    }
    // Fallback to boolVal
    else if (fieldData.boolVal !== undefined) {
      rawValue = fieldData.boolVal
    } else {
      return null
    }

    // Convert various formats to numeric toggle value for internal use
    // ACC stores as: "True"/"False"/"NA" (strings) or true/false (boolean)
    // We convert to: 1 (Yes), 0 (No), 2 (NA) for display
    if (rawValue === true || rawValue === 'true' || rawValue === 'True' || rawValue === 1 || rawValue === '1') {
      return 1
    }
    if (rawValue === false || rawValue === 'false' || rawValue === 'False' || rawValue === 0 || rawValue === '0') {
      return 0
    }
    if (rawValue === 2 || rawValue === '2' || rawValue === 'NA' || rawValue === 'na') {
      return 2
    }

    return rawValue
  }

  // Map value_name to actual property name for other types
  const valueMap = {
    'number_val': 'numberVal',
    'text_val': 'textVal',
    'date_val': 'dateVal',
    'bool_val': 'boolVal',
    'choice_val': 'choiceVal',
    'user_val': 'userVal',
    'list_val': 'listVal'
  }

  const propName = valueMap[valueType] || 'textVal'
  const value = fieldData[propName]

  // For choice fields, handle arrays
  if (valueType === 'choice_val' && Array.isArray(value)) {
    return value.join(', ')
  }

  return value !== undefined ? value : null
}

/**
 * Create field name map from template fields
 * @param {Array} fields - Array of field definitions
 * @returns {Object} Map of fieldId -> label
 */
export const createFieldNameMap = (fields) => {
  const map = {}
  fields.forEach(field => {
    map[field.fieldId] = field.label
  })
  return map
}

/**
 * Group fields by section
 * @param {Array} fields - Array of field definitions
 * @returns {Object} Map of sectionLabel -> fields[]
 */
export const groupFieldsBySection = (fields) => {
  const grouped = {}
  fields.forEach(field => {
    const section = field.sectionLabel || 'General'
    if (!grouped[section]) {
      grouped[section] = []
    }
    grouped[section].push(field)
  })
  return grouped
}

/**
 * Format field value for display
 * @param {*} value - Field value
 * @param {string} valueType - Type of value
 * @returns {string} Formatted value
 */
export const formatFieldValue = (value, valueType) => {
  if (value === null || value === undefined) {
    return '—'
  }

  switch (valueType) {
    case 'number_val':
      return typeof value === 'number' ? value.toLocaleString() : value
    case 'date_val':
      return value ? new Date(value).toLocaleDateString() : '—'
    case 'bool_val':
      return value ? 'Yes' : 'No'
    case 'toggle_val':
      // Toggle values: 0 = No, 1 = Yes, 2 = NA
      // Also handle boolean values (true = Yes, false = No)
      if (value === true || value === 1 || value === '1' || value === 'yes' || value === 'Yes') return 'Yes'
      if (value === false || value === 0 || value === '0' || value === 'no' || value === 'No') return 'No'
      if (value === 2 || value === '2' || value === 'na' || value === 'NA') return 'NA'
      return String(value)
    default:
      return String(value)
  }
}
